// The tickets behind a dashboard KPI card.
//
// Each card states a figure; clicking it lists the tickets that figure is
// about, using the same ticket list endpoint (and therefore the same role
// scoping) as the Tickets page. The counts follow the Dashboard's own rules:
// Open and Total are ALL-TIME (the Dashboard asks analytics for countsAllTime),
// while response time and SLA follow the date picker.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { Modal } from '@centraoneplus/ui';
import type { Ticket } from '@centraoneplus/shared-types';
import { ticketService, type TicketFilters } from '@services/ticket.service';
import { StatusBadge } from '@components/tickets/StatusBadge';
import { PriorityBadge } from '@components/tickets/PriorityBadge';

const PAGE_SIZE = 10;

export interface KpiDrillDownContext {
  dateRange?: { start: string; end: string };
  /** The Dashboard's Team filter (team plus its sub-teams). */
  teamId?: string;
}

interface DrillDef {
  title: string;
  description: (range?: string) => string;
  filters: (ctx: KpiDrillDownContext) => TicketFilters;
  /** Extra column after Created. */
  column?: { label: string; value: (t: Ticket) => string };
}

/** Local-day bounds of the picker's YYYY-MM-DD dates, as ISO instants. */
function rangeFilters(range?: { start: string; end: string }): TicketFilters {
  if (!range?.start || !range?.end) return {};
  return {
    createdAfter: new Date(`${range.start}T00:00:00`).toISOString(),
    createdBefore: new Date(`${range.end}T23:59:59.999`).toISOString(),
  };
}

function formatDuration(ms: number): string {
  if (ms < 60000) return `${Math.max(1, Math.round(ms / 1000))}s`;
  if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
  if (ms < 86400000) return `${(ms / 3600000).toFixed(1)}h`;
  return `${(ms / 86400000).toFixed(1)}d`;
}

function firstReply(t: Ticket): string {
  if (!t.firstResponseAt) return 'Awaiting reply';
  const ms = new Date(t.firstResponseAt).getTime() - new Date(t.createdAt).getTime();
  return Number.isFinite(ms) && ms >= 0 ? formatDuration(ms) : '—';
}

const DRILLS: Record<string, DrillDef> = {
  kpi_open_tickets: {
    title: 'Open tickets',
    description: () => 'Every ticket still new, in progress or pending.',
    filters: () => ({ status: ['new', 'in_progress', 'pending'] }),
  },
  kpi_total_tickets: {
    title: 'All tickets',
    description: () => 'Every ticket, including backtracked ones, newest first.',
    filters: () => ({ includeBacktracked: true }),
  },
  kpi_avg_response_time: {
    title: 'First replies',
    description: (range) => `Tickets created ${range}, with how long each waited for its first reply.`,
    filters: (ctx) => rangeFilters(ctx.dateRange),
    column: { label: 'First reply', value: firstReply },
  },
  kpi_sla_compliance: {
    title: 'SLA breaches',
    description: (range) => `Tickets created ${range} that breached their SLA.`,
    filters: (ctx) => ({ slaBreached: true, ...rangeFilters(ctx.dateRange) }),
    column: { label: 'First reply', value: firstReply },
  },
};

/** Whether a KPI card has a ticket list behind it. */
export function hasKpiDrillDown(kpiType: string): boolean {
  return kpiType in DRILLS;
}

/** The ticket query a KPI card drills into. Exported for tests. */
export function kpiDrillDownFilters(kpiType: string, ctx: KpiDrillDownContext, page = 1): TicketFilters | null {
  const def = DRILLS[kpiType];
  if (!def) return null;
  return {
    ...def.filters(ctx),
    ...(ctx.teamId ? { teamId: ctx.teamId, includeSubTeams: true } : {}),
    page,
    limit: PAGE_SIZE,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  };
}

function rangeLabel(range?: { start: string; end: string }): string {
  if (!range?.start || !range?.end) return 'in this period';
  const fmt = (d: string) =>
    new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return `between ${fmt(range.start)} and ${fmt(range.end)}`;
}

interface KpiDrillDownModalProps extends KpiDrillDownContext {
  kpiType: string;
  isOpen: boolean;
  onClose: () => void;
}

export function KpiDrillDownModal({ kpiType, isOpen, onClose, dateRange, teamId }: KpiDrillDownModalProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const def = DRILLS[kpiType];
  const filters = kpiDrillDownFilters(kpiType, { dateRange, teamId }, page);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['dashboard', 'kpi-drilldown', kpiType, filters],
    queryFn: () => ticketService.getTickets(filters!),
    enabled: isOpen && !!filters,
    placeholderData: keepPreviousData,
  });

  if (!def) return null;

  const tickets = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, data?.totalPages ?? Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  const close = () => {
    setPage(1);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={close} title={def.title} size="xl">
      {/* Capped to the sheet's body (viewport − sheet margin − title bar −
          body padding) so the Modal body itself never scrolls: only the table
          does, and the footer stays pinned beneath it. */}
      <div className="flex max-h-[calc(100vh-8.5rem)] min-h-0 flex-col gap-4">
        <p className="flex-none text-sm text-[var(--co-ink-3)]">
          {def.description(rangeLabel(dateRange))}
          {teamId ? ' Filtered to the selected team.' : ''}
        </p>

        {error ? (
          <div className="flex-none rounded-lg border border-[var(--co-burgundy-line)] bg-[var(--co-burgundy-wash)] p-4 text-sm text-[var(--co-burgundy)]">
            Couldn't load the tickets.{' '}
            <button type="button" className="font-medium underline" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        ) : isLoading ? (
          <div className="flex-none space-y-2" aria-busy="true">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-11 animate-pulse rounded-lg bg-[var(--co-sunken)]" />
            ))}
          </div>
        ) : tickets.length === 0 ? (
          <div className="flex-none rounded-lg border border-dashed border-[var(--co-line)] px-4 py-10 text-center text-sm text-[var(--co-ink-3)]">
            No tickets to show.
          </div>
        ) : (
          <div
            className={`scrollbar-thin min-h-0 flex-1 overflow-auto rounded-lg border border-[var(--co-line)] transition-opacity ${
              isFetching ? 'opacity-60' : ''
            }`}
          >
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="sticky top-0 z-[1] bg-[var(--co-sunken)] text-[11px] uppercase tracking-[.08em] text-[var(--co-ink-3)]">
                <tr>
                  <th className="px-3 py-2 font-semibold">Ticket</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Priority</th>
                  <th className="px-3 py-2 font-semibold">Assignee</th>
                  <th className="px-3 py-2 font-semibold">Created</th>
                  {def.column && <th className="px-3 py-2 font-semibold">{def.column.label}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--co-line-subtle)]">
                {tickets.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => navigate(`/tickets/${t.id}`)}
                    className="cursor-pointer transition-colors hover:bg-[var(--co-selected)]"
                  >
                    <td className="max-w-[280px] px-3 py-2.5">
                      <div className="co-mono text-[11px] text-[var(--co-ink-3)]">{t.ticketNumber}</div>
                      <div className="truncate font-medium text-[var(--co-ink)]" title={t.subject}>
                        {t.subject}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <StatusBadge status={t.status} size="sm" />
                    </td>
                    <td className="px-3 py-2.5">
                      <PriorityBadge priority={t.priority} showLabel size="sm" />
                    </td>
                    <td className="max-w-[160px] truncate px-3 py-2.5 text-[var(--co-ink-2)]">
                      {t.assignee?.name || 'Unassigned'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-[var(--co-ink-2)]">
                      {new Date(t.createdAt).toLocaleDateString(undefined, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    {def.column && (
                      <td className="whitespace-nowrap px-3 py-2.5 text-[var(--co-ink-2)]">{def.column.value(t)}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pinned footer: range + hint on the left, pager on the right. */}
        <div className="flex flex-none flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-[var(--co-line)] pt-3 text-sm text-[var(--co-ink-3)]">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {total > 0 && (
              <span className="tabular-nums">
                {from}–{to} of {total} ticket{total === 1 ? '' : 's'}
              </span>
            )}
            <span className="flex items-center gap-1.5 text-xs">
              <ExternalLink className="h-3.5 w-3.5" />
              Click a ticket to open it.
            </span>
          </div>
          {total > 0 && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || isFetching}
                aria-label="Previous page"
                className="rounded-md p-1.5 transition-colors hover:bg-[var(--co-selected)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-1 tabular-nums">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || isFetching}
                aria-label="Next page"
                className="rounded-md p-1.5 transition-colors hover:bg-[var(--co-selected)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
